import React from 'react';

import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import useCurrentUser from '~/hooks/useCurrentUser';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';

export type PromoActivityFunnelStage = {
  label: string;
  value: number;
};

function getConversionLabel(stage: PromoActivityFunnelStage, previousStage?: PromoActivityFunnelStage): string {
  if (!previousStage) {
    return 'Starting volume';
  }

  if (previousStage.value === 0) {
    return 'No prior volume';
  }

  return `${Math.round((stage.value / previousStage.value) * 100)}% from ${previousStage.label.toLowerCase()}`;
}

/** A compact left-to-right view of the request-to-use path, with accessible step and conversion context. */
export default function PromoActivityFunnelChart({
  stages,
  title = 'Activity funnel',
}: {
  stages: Array<PromoActivityFunnelStage>;
  title?: string;
}) {
  const { user } = useCurrentUser();
  const { hasRole } = useUserPromoRoles({ userId: user?.id_member ?? '', enabled: Boolean(user?.id_member) });
  const isPromoBetaUser = hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);

  if (!isPromoBetaUser) {
    return null;
  }

  return (
    <Paper component="section" variant="outlined" aria-label={title} sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">{title}</Typography>
        <Box
          role="list"
          sx={{
            alignItems: 'stretch',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0.5,
          }}
        >
          {stages.map((stage, index) => {
            const previousStage = stages[index - 1];

            return (
              <Box
                key={stage.label}
                role="listitem"
                sx={(theme) => ({
                  backgroundColor: alpha(theme.palette.primary.main, 0.9 - index * 0.12),
                  clipPath: {
                    xs: 'none',
                    md:
                      index === stages.length - 1
                        ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)'
                        : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
                  },
                  color: theme.palette.primary.contrastText,
                  flex: '1 1 0',
                  minWidth: 0,
                  px: { xs: 2, md: 2.5 },
                  py: 1.5,
                  textAlign: { xs: 'left', md: 'center' },
                })}
              >
                <Typography color="inherit" variant="caption">
                  Step {index + 1} of {stages.length}
                </Typography>
                <Typography color="inherit" variant="body2">
                  {stage.label}
                </Typography>
                <Typography color="inherit" sx={{ fontSize: '1.5rem', fontWeight: 'fontWeightBold', lineHeight: 1.2 }}>
                  {stage.value.toLocaleString()}
                </Typography>
                <Typography color="inherit" sx={{ opacity: 0.9 }} variant="caption">
                  {getConversionLabel(stage, previousStage)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Stack>
    </Paper>
  );
}
